import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ShortcutBinder } from "./shortcut-binder";
import type { Action } from "./types";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface ActionRegistryState {
  actions: Action[];
  register: (actions: Action[]) => () => void;
  paletteOpen: boolean;
  setPaletteOpen: Dispatch<SetStateAction<boolean>>;
  /** The action ID of the currently active sub-page, or `null` for root. */
  activePage: string | null;
  setActivePage: Dispatch<SetStateAction<string | null>>;
}

const ActionRegistryContext = createContext<ActionRegistryState | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/** Wraps the app and owns the canonical action list, palette state, and shortcut bindings. */
export const ActionProvider = ({ children }: PropsWithChildren) => {
  const [actionSets, setActionSets] = useState<Map<symbol, Action[]>>(
    () => new Map(),
  );
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [activePage, setActivePage] = useState<string | null>(null);

  const register = useCallback((actions: Action[]) => {
    const key = Symbol();
    setActionSets((prev) => {
      const next = new Map(prev);
      next.set(key, actions);
      return next;
    });
    return () => {
      setActionSets((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    };
  }, []);

  const actions = useMemo(() => {
    const all: Action[] = [];
    for (const set of actionSets.values()) {
      all.push(...set);
    }
    return all.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }, [actionSets]);

  const value = useMemo<ActionRegistryState>(
    () => ({
      actions,
      register,
      paletteOpen,
      setPaletteOpen,
      activePage,
      setActivePage,
    }),
    [actions, register, paletteOpen, setPaletteOpen, activePage],
  );

  return (
    <ActionRegistryContext.Provider value={value}>
      <ShortcutBinder actions={actions} />
      {children}
    </ActionRegistryContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useActionRegistry() {
  const ctx = useContext(ActionRegistryContext);
  if (!ctx)
    throw new Error("useActionRegistry must be used within <ActionProvider>");
  return ctx;
}

/** Returns the current flat list of actions sorted by priority. */
export const useActions = () => useActionRegistry().actions;

/**
 * Registers actions for the lifetime of the calling component.
 * Actions are added on mount and removed on unmount.
 *
 * @param actions - Array of actions to register.
 * @param deps - Dependency array that triggers re-registration when changed.
 */
export const useRegisterActions = (actions: Action[], deps: unknown[]) => {
  const { register } = useActionRegistry();
  const actionsRef = useRef(actions);
  actionsRef.current = actions;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => register(actionsRef.current), deps);
};

/** Controls the command palette open state and sub-page navigation. */
export const useCommandPalette = () => {
  const { paletteOpen, setPaletteOpen, activePage, setActivePage } =
    useActionRegistry();

  const toggle = useCallback(
    () => setPaletteOpen((prev) => !prev),
    [setPaletteOpen],
  );
  const open = useCallback(() => setPaletteOpen(true), [setPaletteOpen]);
  const close = useCallback(() => {
    setPaletteOpen(false);
    setActivePage(null);
  }, [setPaletteOpen, setActivePage]);

  /** Opens the palette directly to a sub-page. */
  const showPage = useCallback(
    (actionId: string) => {
      setActivePage(actionId);
      setPaletteOpen(true);
    },
    [setActivePage, setPaletteOpen],
  );

  return {
    open: paletteOpen,
    setOpen: setPaletteOpen,
    toggle,
    show: open,
    hide: close,
    activePage,
    setActivePage,
    showPage,
  };
};
